/**
 * HomePage — PHASE II APEX · « Homepage Institutional Upgrade » (09/2026)
 *
 * Récit en huit mouvements, calé sur le standard du Hero cinématique :
 *   HERO → VISION (manifeste) → MARCHÉS (géographies) → MÉTHODE →
 *   RÉALISATIONS → EXPERTISE (ex-discipline, fusionnée) → CONTENU (journal)
 *   → RELATION (deux entrées, coda).
 *
 * La Home reste ENTIÈREMENT pilotée par le CMS site_content
 * (page_id "home", modèle V2 — backend routes/site_content_public_v2.py) :
 *   • `sections._layout`  : ordre + visibilité des mouvements
 *   • `sections.seo`      : title/description par langue
 *   • `sections.hero`     : 6 variantes basculables en 1 clic admin
 *   • chaque mouvement    : textes, images, légendes, CTA éditables
 *
 * Les fallbacks inline sont le MIROIR des defaults backend (rendu immédiat,
 * zéro flash) — la source de vérité est le CMS.
 * Montants pricing : source canonique config/pricing.js (jamais en CMS).
 * Garde anti-SPV (feature flag) : inchangé.
 * Sanctuaires Phase II : H1/H2/H3 et contenus SEO préservés dans le HTML,
 * CTAs et routes inchangés (instrumentation acquisition intacte), données
 * financières et images d'actifs réels strictement conservées.
 */
import { useCallback, useEffect, useState, lazy, Suspense } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { usePublicPageData } from '../../context/PublicPageData';
import { useI18n } from '../../context/I18nContext';
import { localizedPath } from '../../i18n/routes';
import { ArrowRight } from '@phosphor-icons/react';
import { priceLabel } from '../../config/pricing';
import { isPublicSpvOfferEnabled } from '../../config/featureFlags';
import SEOHead from '../../components/SEOHead';
import WMLogo from '../../components/brand/WMLogo';
import { Reveal, MaskReveal } from '../../hooks/useScrollReveal';
import EditorialImage from '../../components/public/EditorialImage';
import CinematicHero from '../../components/public/CinematicHero';
import { projectSlug } from './RealisationsPage';
import HeroTerritories, { territoryIndexAt } from '../../components/public/HeroTerritories';
import { useSiteContent, cmsVal } from '../../hooks/useSiteContent';
import { PullQuote } from '../../components/editorial/PullQuote';
import { EditorialDivider } from '../../components/editorial/EditorialDivider';

/* PUBLIC V2.1 (budget bundle, verrou lot 5) — la section « Registre des
   implantations » (globe + juridictions + registre bilingue inline) vit
   sous la ligne de flottaison : chunk lazy, rendu identique, prerender
   intact (capture après networkidle). */
const GeographiesSectionLazy = lazy(() => import('../../components/public/HomeGeographiesSection'));
/* AUTHORITY V1 (complément Home) — preuve sociale Trustpilot : chunk lazy
   (HomePage est eager/entry point : rien ne doit gonfler main.js). */
const TrustpilotHomeStripLazy = lazy(() =>
  import('../../components/public/TrustpilotNotice').then((m) => ({ default: m.TrustpilotHomeStrip }))
);

/* INSTITUTIONAL DEPTH V1 (LOT 2) — trois mouvements ajoutés au rythme
   existant : methode (la méthode en bref), citation (respiration),
   apercu_prive (ce que contient l'espace privé). Contenu structuré dans
   le code (arbitrage PO 1a) — chaque affirmation est consignée au
   Registre des affirmations du rapport DEPTH V1. */
/* HOMEPAGE PHASE II — ordre canonique aligné sur la narration validée PO :
   HERO → VISION → MARCHÉS → MÉTHODE → RÉALISATIONS → (preuve sociale) →
   (respiration) → EXPERTISE → (citation) → CONTENU → RELATION.
   `journal` (nouveau mouvement CONTENU) est inséré par mergeLayoutOrder à sa
   position canonique même sur les layouts CMS antérieurs. */
const DEFAULT_ORDER = ['hero', 'manifeste', 'geographies', 'methode', 'realisations', 'trustpilot', 'interlude', 'discipline', 'citation', 'journal', 'two_paths', 'pricing', 'apercu_prive', 'coda'];

/* L'ordre stocké en CMS (antérieur à DEPTH V1) ne connaît pas les nouveaux
   mouvements : on les insère à leur position canonique SANS toucher à
   l'ordre administré — zéro régression d'administrabilité. */
const mergeLayoutOrder = (stored, defaults) => {
  const out = stored.filter((id) => defaults.includes(id));
  defaults.forEach((id, i) => {
    if (out.includes(id)) return;
    let anchor = -1;
    for (let j = i - 1; j >= 0; j -= 1) {
      const at = out.indexOf(defaults[j]);
      if (at !== -1) { anchor = at; break; }
    }
    out.splice(anchor + 1, 0, id);
  });
  return out;
};

export default function HomePage() {
  const { lang } = useI18n();
  const fr = lang === 'fr';
  const spvPublicEnabled = isPublicSpvOfferEnabled();
  const { content } = useSiteContent('home');

  const v = (path, fb) => cmsVal(content, `sections.${path}`, fb);
  const arr = (path, fb) => {
    const val = cmsVal(content, `sections.${path}`, null);
    return Array.isArray(val) && val.length ? val : fb;
  };

  const seoTitle = v('seo.title', fr
    ? 'Wealth Management — Global Real Assets · Maison d\u2019investissement en actifs réels'
    : 'Wealth Management — Global Real Assets · Real assets investment house');
  const seoDesc = v('seo.description', fr
    ? 'Opérations immobilières privées — océan Indien, Golfe, Pyrénées, Europe. Club deals, SCI, SPV — réservé aux investisseurs qualifiés et porteurs de projet.'
    : 'Private real estate operations — Indian Ocean, Gulf, Pyrenees, Europe. Club deals, SCI, SPV — reserved for qualified investors and project sponsors.');
  const seoCanonical = `https://www.wealth-management-assets.com/${fr ? 'fr' : 'en'}`;

  const paths = {
    requestAccess: localizedPath('requestAccess', lang),
    about: localizedPath('about', lang),
    createSpv: localizedPath('createSpv', lang),
    contact: localizedPath('contact', lang),
    howItWorks: localizedPath('howItWorks', lang),
  };

  const sectionProps = { fr, paths, v, arr, spvPublicEnabled };
  const SECTIONS = {
    hero: <HeroSection key="hero" {...sectionProps} />,
    trustpilot: (
      <Suspense key="trustpilot" fallback={null}>
        <TrustpilotHomeStripLazy lang={lang} />
      </Suspense>
    ),
    manifeste: <ManifesteSection key="manifeste" {...sectionProps} />,
    methode: <MethodeSection key="methode" {...sectionProps} />,
    geographies: (
      <Suspense
        key="geographies"
        fallback={<section className="relative bg-ivory py-28 lg:py-40" aria-hidden />}
      >
        <GeographiesSectionLazy {...sectionProps} />
      </Suspense>
    ),
    realisations: <RealisationsSection key="realisations" {...sectionProps} />,
    interlude: <InterludeSection key="interlude" {...sectionProps} />,
    discipline: <ExpertiseSection key="discipline" {...sectionProps} />,
    citation: <CitationSection key="citation" {...sectionProps} />,
    journal: <JournalSection key="journal" {...sectionProps} lang={lang} />,
    two_paths: <TwoPathsSection key="two_paths" {...sectionProps} />,
    pricing: spvPublicEnabled ? <PricingSection key="pricing" {...sectionProps} /> : null,
    apercu_prive: <ApercuPriveSection key="apercu_prive" {...sectionProps} />,
    coda: <CodaSection key="coda" {...sectionProps} />,
  };

  const order = mergeLayoutOrder(arr('_layout.order', DEFAULT_ORDER), DEFAULT_ORDER);
  const hidden = arr('_layout.hidden', []);

  return (
    <div className="bg-ivory" data-testid="homepage" data-lot="phase2-institutional-upgrade">
      <SEOHead title={seoTitle} description={seoDesc} canonical={seoCanonical}
        hreflangs={{ fr: '/fr', en: '/en' }} />
      {order.filter((id) => !hidden.includes(id)).map((id) => id === 'hero' ? <div key={id}>{SECTIONS[id]}<AudienceOrientation {...sectionProps} /></div> : SECTIONS[id] || null)}
    </div>
  );
}

function AudienceOrientation({ fr, paths, spvPublicEnabled }) {
  const entries = [
    { number: '01', title: fr ? 'Je souhaite investir' : 'I want to invest', description: fr ? 'Découvrir notre approche et demander un accès investisseur.' : 'Discover our approach and request investor access.', href: paths.requestAccess },
    { number: '02', title: fr ? 'Je porte un projet' : 'I have a project', description: fr ? 'Échanger sur la structuration et le suivi de mon opération.' : 'Discuss structuring and administering my operation.', href: spvPublicEnabled ? paths.createSpv : paths.contact },
  ];
  return <nav className="bg-ivory border-b border-stone" aria-label={fr ? 'Choisir votre parcours' : 'Choose your path'} data-testid="home-orientation">
    <div className="container-editorial grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-stone">
      {entries.map((entry, index) => <Link key={entry.number} to={entry.href} className={`group flex items-center gap-5 py-7 lg:py-9 ${index ? 'md:pl-10 lg:pl-14' : 'md:pr-10 lg:pr-14'} focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-dark`}>
        <span className="font-heading italic text-xl text-gold-dark/70" aria-hidden>{entry.number}</span>
        <span className="flex-1"><span className="block font-heading text-[1.6rem] lg:text-3xl text-navy group-hover:text-gold-dark transition-colors">{entry.title}</span><span className="block mt-1 text-[13px] leading-relaxed text-graphite">{entry.description}</span></span>
        <ArrowRight className="text-gold-dark shrink-0 motion-safe:transition-transform motion-safe:group-hover:translate-x-1" size={20} aria-hidden />
      </Link>)}
    </div>
  </nav>;
}

/* ── Helper : overline + filet or ─────────────────────────────────────── */
export function Overline({ children, dark = false, center = false }) {
  return (
    <div className={`flex items-center gap-3 mb-5 ${center ? 'justify-center' : ''}`}>
      <span className={`inline-block h-px w-6 ${dark ? 'bg-gold' : 'bg-gold-dark'}`} aria-hidden />
      <span className={`text-[11px] tracking-[0.16em] uppercase font-medium ${dark ? 'text-gold' : 'text-gold-dark'}`}>
        {children}
      </span>
    </div>
  );
}

/* ── Helper : folio de section (numérotation éditoriale) ──────────────── */
export function Folio({ n, dark = false }) {
  return (
    <span
      className={`hidden lg:block absolute top-10 right-10 font-heading italic text-xl select-none ${dark ? 'text-gold/30' : 'text-gold-dark/30'}`}
      aria-hidden
    >
      {n}
    </span>
  );
}

/* ═══ 01 · HERO — variantes CMS basculables ════════════════════════════ */
const FB_VARIANT = {
  fr: {
    overline: 'Maison d\u2019investissement en actifs réels',
    title_1: 'Sélectionner rarement. Structurer proprement.',
    title_em: 'Administrer longtemps.',
    lede: 'Nous retenons peu d\u2019opérations. Chacune est logée dans un véhicule dédié, gouvernée par écrit et administrée par la maison — de la première signature à la dernière distribution.',
  },
  en: {
    overline: 'Real assets investment house',
    title_1: 'Select rarely. Structure properly.',
    title_em: 'Administer for the long run.',
    lede: 'We retain few operations. Each one lives in a dedicated vehicle, governed in writing and administered by the house — from first signature to final distribution.',
  },
};

function HeroSection({ fr, paths, v, arr }) {
  const lang = fr ? 'fr' : 'en';
  const variants = arr('hero.variants', []);
  const activeId = v('hero.active_variant', 'v_metier');
  const variant = variants.find((x) => x.id === activeId) || variants[0] || FB_VARIANT[fr ? 'fr' : 'en'];
  const fbv = FB_VARIANT[fr ? 'fr' : 'en'];
  // MISSION HERO VIDÉO — film désactivable en 1 clic CMS (hero.film_enabled=false
  // → le poster premium reste seul, architecture identique, zéro flash).
  const filmEnabled = v('hero.film_enabled', true) !== false;

  // Territoire actif = suit la vidéo automatiquement (index seul en state :
  // re-rendu uniquement aux changements de plan, jamais au timeupdate).
  const [territoryIdx, setTerritoryIdx] = useState(0);
  const onFilmProgress = useCallback((t, duration) => {
    setTerritoryIdx((prev) => {
      const next = territoryIndexAt(t, duration);
      return next === prev ? prev : next;
    });
  }, []);

  return (
    <section
      className="relative min-h-[100svh] -mt-[73px] flex items-center overflow-hidden bg-navy-950"
      data-testid="hero-section"
      data-hero-dark
    >
      <CinematicHero
        poster={v('hero.film_poster', '/images/brand/film/film_poster.webp')}
        posterAvif={v('hero.film_poster_avif', '/images/brand/film/film_poster.avif')}
        posterMobile={v('hero.film_poster_mobile', '/images/brand/film/film_poster_mobile.webp')}
        posterMobileAvif={v('hero.film_poster_mobile_avif', '/images/brand/film/film_poster_mobile.avif')}
        mp4={filmEnabled ? v('hero.film_mp4', '/media/brand_film.mp4') : ''}
        webm={filmEnabled ? v('hero.film_webm', '/media/brand_film.webm') : ''}
        mp4Mobile={filmEnabled ? v('hero.film_mp4_mobile', '/media/brand_film_mobile.mp4') : ''}
        webmMobile={filmEnabled ? v('hero.film_webm_mobile', '/media/brand_film_mobile.webm') : ''}
        alt={v('hero.image_alt', fr
          ? 'Terrasse d\u2019une villa contemporaine au couchant, skyline de Dubaï au loin'
          : 'Contemporary villa terrace at dusk, Dubai skyline in the distance')}
        onProgress={filmEnabled ? onFilmProgress : null}
      />

      {/* Overlay intelligent — directionnel, jamais un voile uniforme :
          lisibilité du texte à gauche, architecture respirante à droite. */}
      <span className="absolute inset-0 bg-gradient-to-r from-navy-950/85 via-navy-950/35 via-40% to-transparent" aria-hidden />
      <span className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-navy-950/85 via-navy-950/40 to-transparent" aria-hidden />
      <span className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-navy-950/55 to-transparent" aria-hidden />

      {/* Filet or vertical signature (gauche) */}
      <span className="absolute left-0 top-1/4 bottom-1/4 w-px bg-gradient-to-b from-transparent via-gold to-transparent" aria-hidden />

      {/* Micro-texte vertical droit — signature éditoriale de la référence */}
      <div className="hidden xl:flex absolute right-9 top-1/2 -translate-y-1/2 z-10 flex-col items-center gap-5" aria-hidden data-testid="hero-vertical-note">
        <span className="w-px h-20 bg-gradient-to-b from-transparent to-ivory/30" />
        <span className="[writing-mode:vertical-rl] text-[10.5px] tracking-[0.3em] uppercase text-ivory/45 select-none">
          {v('hero.vertical_note', fr ? 'Investir aujourd\u2019hui — Bâtir demain' : 'Investing today — Building tomorrow')}
        </span>
        <span className="w-px h-20 bg-gradient-to-b from-ivory/30 to-transparent" />
      </div>

      <div className="container-immersive relative z-10 w-full pt-28 pb-28 md:pt-24 md:pb-20" data-testid="hero-container">
        <div className="max-w-[880px]" data-testid="hero-content-block">
          <Reveal>
            <Overline dark>{variant.overline || fbv.overline}</Overline>
          </Reveal>

          <h1
            className="font-heading font-medium text-ivory text-[2.7rem] sm:text-[3.6rem] lg:text-[4.5rem] xl:text-[5rem] leading-[1.04] tracking-[-0.015em] mb-8 text-balance"
            data-testid="hero-title"
          >
            <MaskReveal delay={100}>{variant.title_1 || fbv.title_1}</MaskReveal>
            <MaskReveal delay={280}>
              <span className="italic text-gold-bright">{variant.title_em || fbv.title_em}</span>
            </MaskReveal>
          </h1>

          <Reveal delay={220}>
            <p className="text-ivory/85 text-lg lg:text-xl leading-relaxed max-w-2xl mb-3">
              {variant.lede || fbv.lede}
            </p>
            <p className="text-ivory/55 text-sm leading-relaxed max-w-2xl mb-10">
              {v('hero.note', fr
                ? 'Réservé aux investisseurs qualifiés et aux porteurs de projet.'
                : 'Reserved for qualified investors and project sponsors.')}
            </p>
          </Reveal>

          <Reveal delay={320}>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4" data-testid="hero-ctas">
              <Link
                to={paths.requestAccess}
                className="inline-flex items-center justify-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-[background-color,transform] duration-200 hover:-translate-y-px"
                data-testid="hero-cta-request-access"
              >
                {v('hero.cta_primary', fr ? 'Demander l\u2019accès' : 'Request access')}
                <ArrowRight size={16} weight="bold" />
              </Link>
              <Link
                to={paths.about}
                className="inline-flex items-center justify-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory border border-ivory/30 hover:border-gold hover:text-gold transition-colors duration-200"
                data-testid="hero-cta-learn-more"
              >
                {v('hero.cta_secondary', fr ? 'Découvrir la maison' : 'Meet the house')}
              </Link>
            </div>
          </Reveal>
        </div>
      </div>

      {/* Barre territoires — l'indicateur suit la vidéo automatiquement.
          Révélation TEMPORELLE (CSS) : les éléments ancrés en bas du hero ne
          franchissent jamais le rootMargin -80px de l'IntersectionObserver. */}
      <div className="absolute inset-x-0 bottom-0 z-10 wm-hero-foot-reveal">
        <div className="container-immersive pb-6 md:pb-7 flex items-end justify-between gap-6">
          <HeroTerritories lang={lang} active={territoryIdx} />
          {/* Indice de scroll — discret, désactivé en reduced-motion */}
          <span className="hidden md:flex flex-col items-center gap-2 pb-1 shrink-0" aria-hidden data-testid="hero-scroll-cue">
            <span className="w-px h-9 bg-gradient-to-b from-transparent via-ivory/40 to-ivory/10 overflow-hidden relative">
              <span className="absolute inset-x-0 top-0 h-3 bg-gold/80 wm-scroll-cue" />
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

/* ═══ 02 · VISION — MANIFESTE + REPÈRES DÉPOSÉS (Phase II · REFINE) ═════
   Composition asymétrique renforcée : manifeste en très grand corps serif
   (drop-cap), colonne de repères en registre à filets, chiffres déposés
   plus imposants. Textes, chiffres et hooks CMS strictement inchangés. */
function ManifesteSection({ fr, v, arr }) {
  const KPI_TESTIDS = ['kpi-deployed', 'kpi-deals', 'kpi-investors', 'kpi-years'];
  const reperes = arr('manifeste.kpis', fr
    ? [
        { value: '95\u202FM€', label: 'déployés depuis l\u2019origine' },
        { value: '29', label: 'opérations menées, de la signature à la sortie' },
        { value: '320', label: 'investisseurs privés à nos côtés' },
        { value: '12', label: 'années de pratique, sans changer de métier' },
      ]
    : [
        { value: '€95M', label: 'deployed since inception' },
        { value: '29', label: 'operations carried from signing to exit' },
        { value: '320', label: 'private investors alongside us' },
        { value: '12', label: 'years of practice, in the same trade' },
      ]);

  return (
    <section className="relative bg-parchment py-28 lg:py-40" data-testid="manifeste-section">
      <Folio n="02" />
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-24 items-start">
          {/* Manifeste — 7 colonnes, grand corps éditorial */}
          <div className="lg:col-span-7">
            <Reveal>
              <Overline>{v('manifeste.overline', fr ? 'Ce que nous croyons' : 'What we believe')}</Overline>
            </Reveal>
            <Reveal delay={100}>
              <p className="drop-cap font-heading text-charcoal text-[1.65rem] sm:text-[2rem] lg:text-[2.3rem] leading-[1.42]" data-testid="manifeste-text">
                {v('manifeste.text', fr
                  ? 'Nous croyons aux actifs qu\u2019on peut visiter. Un hôtel s\u2019éprouve une nuit durant. Un chantier s\u2019écoute, le matin, quand les coffrages s\u2019ouvrent. Un immeuble se lit dans ses parties communes. Cette matérialité discipline nos décisions\u00A0: nous refusons dix-neuf opérations sur vingt — et nous engageons notre propre capital dans chacune de celles que nous retenons.'
                  : 'We believe in assets you can visit. A hotel is tested over a night. A working site is listened to, in the morning, when the formwork opens. A building is read in its common areas. That materiality disciplines our decisions: we decline nineteen operations out of twenty — and we commit our own capital to every one we retain.')}
              </p>
            </Reveal>
            <Reveal delay={260}>
              <span className="block w-14 h-px bg-gold-dark/60 mt-10" aria-hidden />
            </Reveal>
          </div>

          {/* Repères — 5 colonnes, registre à filets */}
          <div className="lg:col-span-5 lg:pt-8" data-testid="kpi-strip">
            <Reveal>
              <p className="text-[10.5px] tracking-[0.18em] uppercase font-semibold text-graphite/70 mb-4">
                {fr ? 'Repères déposés' : 'Figures on record'}
              </p>
            </Reveal>
            <ul className="divide-y divide-stone border-y border-stone">
              {reperes.map(({ value, label }, i) => (
                <Reveal key={KPI_TESTIDS[i] || i} delay={i * 110} as="li">
                  <div className="flex items-baseline gap-6 py-6" data-testid={KPI_TESTIDS[i] || `kpi-${i}`}>
                    <span className="font-heading text-charcoal text-[2.6rem] lg:text-[3rem] leading-none lining-nums tabular-nums tracking-[-0.02em] w-32 shrink-0">
                      {value}
                    </span>
                    <span className="text-graphite text-sm leading-snug">{label}</span>
                  </div>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={480}>
              <p className="text-xs text-graphite/80 leading-relaxed mt-4">
                {v('manifeste.kpi_note', fr
                  ? 'Chiffres cumulés à fin 2025, toutes opérations confondues. Les performances passées ne préjugent pas des performances futures.'
                  : 'Cumulative figures as of end-2025, all operations combined. Past performance is not indicative of future results.')}
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}


/* ═══ 05 · NOS RÉALISATIONS — la preuve par l'œuvre (Phase II · REFINE) ═
   Données 100 % réelles : projets publics du CMS (homepage-projects).
   Aucune invention — noms, lieux et statuts viennent du Studio.
   Mise en scène galerie : cartels sous les images (numéral, nom, lieu,
   statut), filets ivoire, hover discret — plus d'overlay gradient. */
function RealisationsSection({ fr, v }) {
  const publicData = usePublicPageData();
  const [projects, setProjects] = useState(publicData?.projects?.filter(p => p.images?.length).slice(0, 3) || null);

  useEffect(() => {
    let alive = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/homepage-projects/public`)
      .then((res) => {
        if (!alive) return;
        const items = res.data?.projects || (Array.isArray(res.data) ? res.data : []);
        setProjects(items.filter((p) => p.images?.length).slice(0, 3));
      })
      .catch(() => { if (alive) setProjects([]); });
    return () => { alive = false; };
  }, []);

  // État honnête : sans matière réelle, la section ne se rend pas.
  if (!projects || projects.length < 2) return null;
  const [lead, ...rest] = projects;
  const realisationsPath = fr ? '/fr/realisations' : '/en/track-record';

  /* Cartel de galerie — numéral, nom, lieu, statut : la vérité de l'actif,
     posée sous l'image comme dans une salle d'exposition. */
  const Cartel = ({ n, name, location, badge, compact = false }) => (
    <div className={`flex items-baseline justify-between gap-4 border-t border-ivory/15 ${compact ? 'mt-4 pt-3.5' : 'mt-5 pt-4'}`}>
      <div className="flex items-baseline gap-4 min-w-0">
        <span className="font-heading italic text-gold/70 text-lg leading-none shrink-0" aria-hidden>{n}</span>
        <div className="min-w-0">
          <p className={`font-heading text-ivory leading-tight transition-colors duration-300 group-hover:text-gold-bright ${compact ? 'text-lg lg:text-xl' : 'text-xl lg:text-2xl'}`}>
            {name}
          </p>
          {location && <p className="text-ivory/55 text-[12.5px] mt-1">{location}</p>}
        </div>
      </div>
      {badge && (
        <span className="hidden sm:inline-block text-[10px] tracking-[0.14em] uppercase text-gold shrink-0">{badge}</span>
      )}
    </div>
  );

  return (
    <section className="relative bg-navy-deep py-28 lg:py-40 overflow-hidden" data-testid="realisations-section">
      <Folio n="05" dark />
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end mb-14 lg:mb-16">
          <div className="lg:col-span-8">
            <Reveal>
              <Overline dark>{v('realisations.overline', fr ? 'Nos réalisations' : 'Track record')}</Overline>
            </Reveal>
            <h2 className="font-heading font-medium text-ivory text-3xl sm:text-4xl lg:text-[3.2rem] leading-[1.08] mb-6 max-w-3xl">
              <MaskReveal>{v('realisations.title', fr ? 'Ce que la maison a déjà bâti.' : 'What the house has already built.')}</MaskReveal>
            </h2>
            <Reveal delay={150}>
              <p className="text-ivory/70 text-lg leading-relaxed max-w-2xl">
                {v('realisations.intro', fr
                  ? 'Chaque opération est un lieu réel, un chantier mené et des comptes rendus. En voici trois — les autres se visitent.'
                  : 'Every operation is a real place, a completed worksite and written reporting. Here are three — the rest can be visited.')}
              </p>
            </Reveal>
          </div>
          <Reveal delay={220} className="lg:col-span-4 lg:text-right">
            <Link
              to={realisationsPath}
              className="inline-flex items-center gap-3 text-sm tracking-[0.05em] uppercase font-medium text-gold hover:text-gold-bright transition-colors duration-200"
              data-testid="realisations-cta"
            >
              {v('realisations.cta', fr ? 'Parcourir les réalisations' : 'Browse the track record')}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </Reveal>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12" data-testid="realisations-grid">
          <Link to={`${realisationsPath}/${projectSlug(lead)}`} className="lg:col-span-7 group block" data-testid="realisation-lead">
            <Reveal effect="blur">
              <div className="relative overflow-hidden">
                <img
                  src={lead.images[0]}
                  alt={`${lead.name} — ${lead.location || ''}`}
                  loading="lazy"
                  className="w-full h-[300px] sm:h-[380px] lg:h-[460px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
            </Reveal>
            <Cartel n="01" name={lead.name} location={lead.location} badge={lead.badge} />
          </Link>

          <div className="lg:col-span-5 flex flex-col gap-10 lg:gap-8 lg:justify-between">
            {rest.map((p, i) => (
              <Link key={p.id} to={`${realisationsPath}/${projectSlug(p)}`} className="group block" data-testid={`realisation-${i + 1}`}>
                <Reveal effect="blur" delay={150 * (i + 1)}>
                  <div className="relative overflow-hidden">
                    <img
                      src={p.images[0]}
                      alt={`${p.name} — ${p.location || ''}`}
                      loading="lazy"
                      className="w-full h-[220px] lg:h-[168px] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
                    />
                  </div>
                </Reveal>
                <Cartel n={`0${i + 2}`} name={p.name} location={p.location} badge={p.badge} compact />
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ INTERLUDE — le silence du récit (Phase II · KEEP, micro-polish) ═══
   L'énoncé clair validé PO, suivi des critères qui décident. Signature
   ajoutée : le ratio « 1/20 » en filigrane serif — donnée déjà publiée,
   purement décorative (aria-hidden). */
function InterludeSection({ fr, v }) {
  return (
    <section className="relative bg-ivory py-32 lg:py-44 overflow-hidden" data-testid="interlude-section" aria-label={fr ? 'Interlude' : 'Interlude'}>
      <span
        className="hidden lg:block absolute right-[4%] top-1/2 -translate-y-1/2 font-heading italic text-gold-dark/[0.09] text-[11rem] leading-none select-none"
        aria-hidden
      >
        1/20
      </span>
      <div className="container-editorial relative">
        <div className="max-w-4xl lg:ml-[16%]">
          <MaskReveal>
            <p className="font-heading text-charcoal text-[1.7rem] sm:text-[2.2rem] lg:text-[2.6rem] leading-[1.3] tracking-[-0.01em] text-balance" data-testid="interlude-text">
              <span className="inline-block align-middle w-10 h-px bg-gold-dark mr-6 -mt-1" aria-hidden />
              {v('interlude.text', fr
                ? 'Nous ne retenons qu\u2019environ un projet sur vingt étudiés.'
                : 'We retain only about one project in twenty reviewed.')}
            </p>
          </MaskReveal>
          <Reveal delay={250}>
            <p className="text-graphite/85 text-sm lg:text-base leading-relaxed mt-8 lg:pl-16 max-w-2xl" data-testid="interlude-criteria">
              {fr
                ? 'Les critères qui décident sont publics\u00A0: un actif qui se visite, des titres qui se vérifient, une gouvernance écrite — et notre propre capital engagé dans chaque opération retenue.'
                : 'The deciding criteria are public: an asset you can visit, titles that can be verified, written governance — and our own capital committed to every operation retained.'}
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══ 06 · EXPERTISE (Phase II · MERGE, ex-Discipline) ═════════════════
   Fusion validée PO (05/09) : la redondance Méthode/Discipline disparaît.
   Le mouvement devient la présence terrain de la maison — image chantier
   (actif réel), l'engagement du co-investissement en exergue, le travail
   de sélection en contrepoint. GARDE-FOU SEO §27 : les textes des actes
   I (« Une opération retenue sur vingt étudiées… c'est le travail ») et
   III (« Nous suivons les chantiers… nous co-investissons ») restent
   VERBATIM dans le HTML ci-dessous ; l'acte II (structuration) est couvert
   mot pour mot par le mouvement Méthode (temps II + preuves) et par
   /fr/comment-ca-marche · /en/how-it-works. Id CMS `discipline`, testids
   et CTA instrumenté inchangés. */
function ExpertiseSection({ fr, paths, v }) {
  return (
    <section className="relative bg-navy-deep py-28 lg:py-40 overflow-hidden" data-testid="discipline-section">
      <span className="absolute inset-x-0 top-0 h-px bg-gold/40" aria-hidden />
      <Folio n="06" dark />
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Image chantier — 5 colonnes (actif réel, jamais remplacée) */}
          <div className="lg:col-span-5">
            <EditorialImage
              src={v('discipline.image', '/images/brand/chantier_grues_crepuscule.webp')}
              alt={v('discipline.image_alt', fr ? 'Grues d\u2019un chantier côtier au crépuscule' : 'Coastal construction site cranes at dusk')}
              ratio="4/5"
              caption={
                <span className="text-ivory/70">
                  {v('discipline.image_caption', fr
                    ? 'Un chantier du portefeuille, au moment où l\u2019investissement se fait.'
                    : 'A portfolio site, at the moment the investment is being made.')}
                </span>
              }
              testid="discipline-image"
            />
          </div>

          {/* L'engagement — 7 colonnes */}
          <div className="lg:col-span-7 lg:pl-4">
            <Reveal>
              <Overline dark>{v('discipline.overline', fr ? 'Notre discipline' : 'Our discipline')}</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading font-medium text-ivory text-[2.1rem] sm:text-[2.6rem] lg:text-[2.9rem] leading-[1.1] tracking-[-0.01em] mb-10 text-balance">
                {v('discipline.title', fr
                  ? 'L\u2019investissement, pendant qu\u2019il se fait.'
                  : 'The investment, while it is being made.')}
              </h2>
            </Reveal>
            <MaskReveal delay={180}>
              <p className="font-heading text-ivory text-[1.4rem] sm:text-[1.6rem] lg:text-[1.8rem] leading-[1.4] mb-8 max-w-2xl" data-testid="discipline-engagement">
                <span className="inline-block align-middle w-8 h-px bg-gold mr-5 -mt-1" aria-hidden />
                {fr
                  ? 'Nous suivons les chantiers, les baux et les flux comme s\u2019il s\u2019agissait de notre argent. C\u2019est d\u2019ailleurs le cas\u00A0: nous co-investissons.'
                  : 'We track the works, the leases and the cashflows as if it were our own money. It is: we co-invest.'}
              </p>
            </MaskReveal>
            <Reveal delay={280}>
              <p className="text-ivory/70 text-[15px] leading-relaxed max-w-xl mb-10 lg:pl-[52px]" data-testid="discipline-terrain">
                {fr
                  ? 'Une opération retenue sur vingt étudiées\u00A0: nous visitons, nous contre-expertisons, nous renonçons souvent — c\u2019est le travail. Le reste — méthode, véhicule, gouvernance — se lit étape par étape.'
                  : 'One operation retained out of twenty reviewed: we visit, we counter-appraise, we walk away often — that is the job. The rest — method, vehicle, governance — reads step by step.'}
              </p>
            </Reveal>
            <Reveal delay={380}>
              <Link
                to={paths.howItWorks}
                className="inline-flex items-center gap-2 text-sm tracking-[0.06em] uppercase font-medium text-gold hover:text-ivory transition-colors duration-200"
                data-testid="discipline-cta"
              >
                {v('discipline.cta_label', fr ? 'Le parcours, étape par étape' : 'The journey, step by step')}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ 05 · DEUX ENTRÉES — PUBLIC EXPERIENCE V2 : deux parcours COMPLETS ═
   Chaque entrée raconte le parcours réel de bout en bout (qui fait quoi,
   ce que vous recevez, ce que vous voyez dans l'espace privé) — calqué sur
   le produit. Les étapes tolèrent les deux formats CMS : chaîne simple
   (legacy) ou { title, desc } (V2). */
const asStep = (s) => (typeof s === 'string' ? { title: s, desc: null } : s);

/* V2.1 — chaque étape répond à trois questions : ce qui se passe (desc),
   QUI AGIT (who), CE QUE VOUS RECEVEZ (you). Fail-soft : sans who/you
   (CMS legacy), seule la description est rendue. */
const StepMeta = ({ step, fr }) => {
  if (!step.who && !step.you) return null;
  return (
    <span className="block text-[11.5px] leading-relaxed mt-1.5 text-graphite/70">
      {step.who && (
        <>
          <span className="font-semibold text-gold-dark/90 uppercase tracking-[0.08em] text-[9.5px]">{fr ? 'Qui agit' : 'Who acts'}</span>
          {' '}{step.who}
        </>
      )}
      {step.who && step.you && <span className="mx-2 text-gold-dark/50" aria-hidden>·</span>}
      {step.you && (
        <>
          <span className="font-semibold text-gold-dark/90 uppercase tracking-[0.08em] text-[9.5px]">{fr ? 'Vous recevez' : 'You receive'}</span>
          {' '}{step.you}
        </>
      )}
    </span>
  );
};

function TwoPathsSection({ fr, paths, v, arr, spvPublicEnabled }) {
  /* Phase II · SIMPLIFY (GO PO 05/09) — cinq temps essentiels par parcours.
     Le détail complet reste publié et crawlable : Fonctionnement (investisseur,
     trois actes + tableau + FAQ) et Créer une SPV (porteur, offre détaillée).
     Fallbacks = MIROIR des defaults backend (V3_HOME_PARCOURS). */
  const investorSteps = arr('two_paths.investor_steps', fr
    ? [
        { title: 'Demande d\u2019accès', desc: 'Vous présentez votre profil, votre pays, votre horizon. La maison examine chaque demande — certaines sont déclinées.', who: 'Vous, puis la maison', you: 'Une réponse personnelle sous 48 heures ouvrées.' },
        { title: 'Espace privé & vérification d\u2019identité', desc: 'Vos identifiants personnels, protégés par double authentification. KYC/KYB documenté — pièces déposées une fois, conservées et datées.', who: 'Vous, guidé par la maison', you: 'Un espace ouvert, un dossier d\u2019identité validé et daté.' },
        { title: 'Opportunités en privé', desc: 'Chaque opération retenue se présente dossier complet\u00A0: lieu, véhicule dédié, gouvernance, documents.', who: 'La maison', you: 'Le dossier complet de chaque opération retenue.' },
        { title: 'Souscription, signature, virement', desc: 'Bulletin signé, instructions bancaires claires — chaque virement est rapproché puis confirmé dans vos mouvements.', who: 'Vous, avec la maison', you: 'Votre bulletin archivé, chaque virement confirmé.' },
        { title: 'Reporting, distributions, sortie', desc: 'Comptes rendus datés, patrimoine consolidé au centime, distributions suivies — jusqu\u2019à la clôture du véhicule, archives conservées.', who: 'La maison', you: 'Des comptes rendus datés, chaque distribution notifiée, les archives.' },
      ]
    : [
        { title: 'Access request', desc: 'You present your profile, country and horizon. The house reviews every request — some are declined.', who: 'You, then the house', you: 'A personal reply within 48 business hours.' },
        { title: 'Private space & identity verification', desc: 'Your personal credentials, protected by two-factor authentication. Documented KYC/KYB — documents filed once, kept and dated.', who: 'You, guided by the house', you: 'An open space, a validated and dated identity file.' },
        { title: 'Opportunities in private', desc: 'Each retained operation is presented as a full file: place, dedicated vehicle, governance, documents.', who: 'The house', you: 'The full file of every retained operation.' },
        { title: 'Subscription, signature, transfer', desc: 'Signed form, clear banking instructions — every transfer is reconciled then confirmed in your movements.', who: 'You, with the house', you: 'Your form archived, every transfer confirmed.' },
        { title: 'Reporting, distributions, exit', desc: 'Dated reports, holdings consolidated to the cent, distributions tracked — until the vehicle closes, archives kept.', who: 'The house', you: 'Dated reports, every distribution notified, the archives.' },
      ]).map(asStep);
  const sponsorItems = arr('two_paths.sponsor_items', fr
    ? [
        { title: 'Premier échange', desc: 'Vous présentez l\u2019opération — sans engagement, sous confidentialité.', who: 'Vous', you: 'Un premier avis, sous confidentialité.' },
        { title: 'Cadrage & structuration', desc: 'Juridiction, gouvernance, calendrier\u00A0: le véhicule se dessine par écrit avant toute constitution.', who: 'La maison, avec vous', you: 'Un cadrage écrit — juridiction, gouvernance, calendrier.' },
        { title: 'Constitution & documentation', desc: 'Le véhicule est constitué\u00A0; statuts, pactes et bulletins sont complets avant le premier euro appelé.', who: 'La maison', you: 'Le véhicule constitué, une documentation complète.' },
        { title: 'Onboarding des investisseurs', desc: 'Invitations, KYC/KYB, souscriptions — chaque entrée suit le même protocole documenté.', who: 'La maison', you: 'Des investisseurs vérifiés, des souscriptions documentées.' },
        { title: 'Administration, reporting, distributions', desc: 'La maison tient les livres, adresse les comptes rendus à vos investisseurs et suit chaque distribution au centime — jusqu\u2019à la clôture éventuelle.', who: 'La maison', you: 'Les livres tenus, des distributions suivies au centime.' },
      ]
    : [
        { title: 'First conversation', desc: 'You present the operation — without commitment, under confidentiality.', who: 'You', you: 'A first opinion, under confidentiality.' },
        { title: 'Scoping & structuring', desc: 'Jurisdiction, governance, calendar: the vehicle takes shape in writing before any incorporation.', who: 'The house, with you', you: 'A written scoping — jurisdiction, governance, calendar.' },
        { title: 'Incorporation & documentation', desc: 'The vehicle is incorporated; articles, agreements and forms are complete before the first euro is called.', who: 'The house', you: 'The vehicle incorporated, complete documentation.' },
        { title: 'Investor onboarding', desc: 'Invitations, KYC/KYB, subscriptions — every entry follows the same documented protocol.', who: 'The house', you: 'Verified investors, documented subscriptions.' },
        { title: 'Administration, reporting, distributions', desc: 'The house keeps the books, addresses reports to your investors and tracks every distribution to the cent — until the possible closing.', who: 'The house', you: 'Books kept, distributions tracked to the cent.' },
      ]).map(asStep);

  return (
    <section className="relative bg-ivory py-20 lg:py-28" data-testid="audiences-section">
      <Folio n="08" />
      <div className="container-editorial">
        <div className="max-w-3xl mb-16 lg:mb-20">
          <Reveal>
            <Overline>{v('two_paths.overline', fr ? 'Deux manières d\u2019entrer' : 'Two ways in')}</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading font-medium text-ink text-[2.1rem] sm:text-[2.7rem] lg:text-[3rem] leading-[1.08] tracking-[-0.01em] text-balance">
              {v('two_paths.title', fr
                ? 'Nous investissons à vos côtés. Ou nous administrons votre véhicule.'
                : 'We invest alongside you. Or we administer your vehicle.')}
            </h2>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-0">
          {/* Investisseurs — 7 colonnes, dominant */}
          <Reveal className="lg:col-span-7 lg:pr-16 lg:border-r lg:border-stone">
            <article data-testid="audience-card-investor">
              <p className="font-heading italic text-gold-dark text-lg mb-4">01</p>
              <h3 className="font-heading text-charcoal text-[1.7rem] lg:text-[2rem] leading-tight mb-5">
                {v('two_paths.investor_title', fr ? 'Investisseurs privés qualifiés' : 'Qualified private investors')}
              </h3>
              <p className="text-graphite text-base leading-relaxed max-w-xl mb-10">
                {v('two_paths.investor_body', fr
                  ? 'Vous accédez aux opérations que nous avons retenues — dossier complet, véhicule dédié, co-investissement de la maison. Puis tout se suit en un seul endroit\u00A0: souscriptions, mouvements, documents, reporting.'
                  : 'You access the operations we have retained — full file, dedicated vehicle, the house co-investing. Then everything is followed in one place: subscriptions, cashflows, documents, reporting.')}
              </p>
              <details className="border-y border-stone mb-6">
              <summary className="cursor-pointer py-4 text-sm font-medium text-gold-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-dark">
                {fr ? 'Le parcours, résumé en cinq temps' : 'The journey, summarised in five movements'}
              </summary>
              <ol className="space-y-0 divide-y divide-stone border-y border-stone max-w-xl mb-6">
                {investorSteps.map((step, i) => (
                  <li key={i} className="flex items-baseline gap-5 py-4" data-testid={`investor-step-${i + 1}`}>
                    <span className="font-heading italic text-gold-dark/70 text-xl w-7 shrink-0 lining-nums">{i + 1}.</span>
                    <span>
                      <span className="block text-charcoal/95 text-[15px] font-medium">{step.title}</span>
                      {step.desc && <span className="block text-graphite/80 text-[13px] leading-relaxed mt-1">{step.desc}</span>}
                      <StepMeta step={step} fr={fr} />
                    </span>
                  </li>
                ))}
              </ol>
              </details>
              {/* Renvoi crawlable vers le détail complet (Fonctionnement, 3 actes) */}
              <Link
                to={paths.howItWorks}
                className="inline-flex items-center gap-2 mb-8 text-[12px] tracking-[0.06em] uppercase font-medium text-gold-dark hover:text-ink transition-colors duration-200"
                data-testid="two-paths-investor-journey-link"
              >
                {fr ? 'Le parcours complet, étape par étape' : 'The full journey, step by step'}
                <ArrowRight size={12} weight="bold" />
              </Link>
              <p className="text-xs text-graphite/80 mb-8">
                {v('two_paths.investor_note', fr
                  ? 'Ticket d\u2019entrée à partir de 10\u00A0000\u00A0€, selon l\u2019opération.'
                  : 'Entry ticket from \u20AC10,000, depending on the operation.')}
              </p>
              <Link
                to={paths.requestAccess}
                className="inline-flex items-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-[background-color,transform] duration-200 hover:-translate-y-px"
                data-testid="two-paths-cta-investor"
              >
                {v('two_paths.investor_cta', fr ? 'Demander l\u2019accès' : 'Request access')}
                <ArrowRight size={16} weight="bold" />
              </Link>
            </article>
          </Reveal>

          {/* Porteurs — 5 colonnes */}
          <Reveal delay={150} className="lg:col-span-5 lg:pl-16 pt-14 lg:pt-0">
            <article data-testid="audience-card-sponsor">
              <p className="font-heading italic text-gold-dark text-lg mb-4">02</p>
              <h3 className="font-heading text-charcoal text-[1.7rem] lg:text-[2rem] leading-tight mb-5">
                {v('two_paths.sponsor_title', fr ? 'Porteurs de projet' : 'Project sponsors')}
              </h3>
              <p className="text-graphite text-base leading-relaxed mb-10">
                {v('two_paths.sponsor_body', fr
                  ? 'Vous portez une opération et des investisseurs à réunir. La maison structure et administre votre véhicule\u00A0: constitution, gouvernance, onboarding et KYC, documentation, souscriptions et reporting — sans commission sur le capital levé.'
                  : 'You carry an operation and investors to bring together. The house structures and administers your vehicle: incorporation, governance, onboarding and KYC, documentation, subscriptions and reporting — with no commission on the capital raised.')}
              </p>
              <details className="border-y border-stone mb-8">
              <summary className="cursor-pointer py-4 text-sm font-medium text-gold-dark focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-dark">
                {fr ? 'De la première conversation à la clôture' : 'From first conversation to closing'}
              </summary>
              <ol className="space-y-0 divide-y divide-stone/70 border-y border-stone/70 mb-10">
                {sponsorItems.map((item, i) => (
                  <li key={i} className="flex items-baseline gap-4 py-4" data-testid={`sponsor-step-${i + 1}`}>
                    <span className="font-heading italic text-gold-dark/70 text-xl w-7 shrink-0 lining-nums">{i + 1}.</span>
                    <span>
                      <span className="block text-charcoal/90 text-[15px] font-medium">{item.title}</span>
                      {item.desc && <span className="block text-graphite/75 text-[13px] leading-relaxed mt-1">{item.desc}</span>}
                      <StepMeta step={item} fr={fr} />
                    </span>
                  </li>
                ))}
              </ol>
              </details>
              <Link
                to={spvPublicEnabled ? paths.createSpv : paths.contact}
                className="inline-flex items-center gap-2 text-sm tracking-[0.06em] uppercase font-medium text-gold-dark hover:text-ink transition-colors duration-200"
                data-testid="two-paths-cta-sponsor"
              >
                {spvPublicEnabled
                  ? v('two_paths.sponsor_cta_spv', fr ? 'Structurer mon club deal' : 'Structure my club deal')
                  : v('two_paths.sponsor_cta_contact', fr ? 'Nous parler de votre projet' : 'Talk to us about your project')}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </article>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ═══ 06 · TARIFICATION SPV (flag) ═════════════════════════════════════ */
function PricingSection({ fr, paths, v, arr }) {
  // Source canonique des MONTANTS : config/pricing.js — jamais le CMS.
  const PRICE_KEYS = ['spvCreation', 'spvAnnualAdmin', 'spvClosing'];
  const PRICE_TESTIDS = ['homepage-pricing-creation', 'homepage-pricing-admin', 'homepage-pricing-closing'];
  const jalons = arr('pricing.items', fr
    ? [
        { overline: 'À la constitution', title: 'Création & structuration', cadence: 'Montant unique — HT', body: 'Constitution du véhicule, documents fondateurs, coordination administrative et première phase d\u2019onboarding.' },
        { overline: 'Chaque année', title: 'Administration annuelle', cadence: 'Par an — HT · selon périmètre', body: 'Domiciliation, tenue comptable, suivi juridique récurrent, obligations annuelles et reporting.' },
        { overline: 'À la dissolution', title: 'Clôture de la SPV', cadence: 'Montant unique — HT', body: 'Formalités de dissolution, distribution finale et archivage réglementaire.' },
      ]
    : [
        { overline: 'At setup', title: 'Creation & structuring', cadence: 'One-off — excl. tax', body: 'Vehicle setup, founding documents, administrative coordination and initial onboarding phase.' },
        { overline: 'Each year', title: 'Annual administration', cadence: 'Per year — excl. tax · scope-dependent', body: 'Domiciliation, accounting, ongoing legal monitoring, annual obligations and reporting.' },
        { overline: 'At dissolution', title: 'SPV closing', cadence: 'One-off — excl. tax', body: 'Dissolution formalities, final distribution and regulatory archiving.' },
      ]);

  return (
    <section className="relative bg-parchment py-20 lg:py-28" data-testid="pricing-section">
      <Folio n="09" />
      <div className="container-editorial">
        <div className="max-w-3xl mb-16">
          <Reveal>
            <Overline>{v('pricing.overline', fr ? 'Tarification' : 'Pricing')}</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading font-medium text-ink text-[2.1rem] sm:text-[2.6rem] leading-[1.1] tracking-[-0.01em] mb-5 text-balance">
              {v('pricing.title', fr ? 'Trois jalons. Pas de ligne cachée.' : 'Three milestones. No hidden line.')}
            </h2>
            <p className="text-graphite text-base lg:text-lg leading-relaxed">
              {v('pricing.subtitle', fr
                ? 'La structuration d\u2019un club deal se paie à trois moments de la vie du véhicule — et à ces trois moments seulement.'
                : 'Structuring a club deal is paid at three moments in the life of the vehicle — and at those three moments only.')}
            </p>
          </Reveal>
        </div>

        {/* Trois jalons — colonnes à filets (pas de cards boxées) */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 md:divide-x divide-stone border-y border-stone"
          data-testid="homepage-pricing-cards"
        >
          {jalons.slice(0, 3).map(({ overline, title, cadence, body }, i) => (
            <Reveal key={PRICE_TESTIDS[i]} delay={i * 130}>
              <article className="py-10 md:px-10 first:md:pl-0 last:md:pr-0 h-full" data-testid={PRICE_TESTIDS[i]}>
                <p className="text-[11px] tracking-[0.16em] uppercase font-medium text-gold-dark mb-4">{overline}</p>
                <h3 className="font-heading text-charcoal text-[1.35rem] leading-tight mb-6">{title}</h3>
                <div className="font-heading text-ink text-[2.6rem] lg:text-[3rem] leading-none lining-nums tabular-nums tracking-[-0.02em] mb-2">
                  {priceLabel(PRICE_KEYS[i], fr ? 'fr' : 'en')}
                </div>
                <p className="text-[11px] tracking-[0.05em] uppercase font-medium text-graphite mb-5">{cadence}</p>
                <p className="text-sm text-graphite leading-relaxed">{body}</p>
              </article>
            </Reveal>
          ))}
        </div>

        {/* PUBLIC EXPERIENCE V2 — la philosophie derrière la grille.
            Démonstration (pas une liste de prix) : chaque « pourquoi »
            explicite la politique déjà publiée (rémunération adossée au
            travail, aucun frais de levée). Registre : R-V2-05. */}
        <div className="mt-10">
          <Reveal>
            <Overline>{fr ? 'La philosophie de la grille' : 'The philosophy behind the grid'}</Overline>
          </Reveal>
        </div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-4" data-testid="pricing-philosophy">
          {(fr ? [
            {
              q: 'Pourquoi trois jalons\u00A0?',
              a: 'Parce que la structuration a trois moments réels\u00A0: la naissance du véhicule, sa vie administrative, sa dissolution. Payer à ces moments-là, c\u2019est payer un travail qui existe — pas un pourcentage qui s\u2019invente.',
            },
            {
              q: 'Pourquoi aucun frais de placement\u00A0?',
              a: 'Parce que le capital réuni appartient à l\u2019opération. Une commission sur la levée rémunérerait la collecte plutôt que le travail — elle pousserait à lever plus, pas à structurer mieux.',
            },
            {
              q: 'Pourquoi cette structure est-elle plus saine\u00A0?',
              a: 'Parce que la rémunération de la maison ne dépend pas du volume collecté. Que le véhicule soit modeste ou important, le travail se paie au même prix — les intérêts restent alignés\u00A0: les nôtres, les vôtres, ceux de vos investisseurs.',
            },
            {
              q: 'En quoi protège-t-elle les investisseurs\u00A0?',
              a: 'Les coûts sont connus d\u2019avance et publiés\u00A0; le capital souscrit va à l\u2019opération, pas à la collecte\u00A0; et la gouvernance écrite du véhicule, avec le reporting suivi dans l\u2019espace privé, rend chaque euro traçable — du virement à la distribution.',
            },
          ] : [
            {
              q: 'Why three milestones?',
              a: 'Because structuring has three real moments: the vehicle\u2019s birth, its administrative life, its dissolution. Paying at those moments means paying for work that exists — not for a percentage that gets invented.',
            },
            {
              q: 'Why no placement fees?',
              a: 'Because the capital raised belongs to the operation. A commission on the raise would reward collection rather than work — it would push to raise more, not to structure better.',
            },
            {
              q: 'Why is this structure healthier?',
              a: 'Because the house\u2019s compensation does not depend on the volume collected. Whether the vehicle is modest or large, the work is paid the same — interests stay aligned: ours, yours, your investors\u2019.',
            },
            {
              q: 'How does it protect investors?',
              a: 'Costs are known in advance and published; subscribed capital goes to the operation, not to the raise; and the vehicle\u2019s written governance, with reporting followed in the private space, makes every euro traceable — from transfer to distribution.',
            },
          ]).map((item, i) => (
            <Reveal key={i} delay={i * 120}>
              <details className="border-b border-stone pb-4" data-testid={`pricing-philosophy-${i + 1}`}>
                <summary className="cursor-pointer font-heading text-charcoal text-lg lg:text-xl leading-snug py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold-dark">{item.q}</summary>
                <p className="text-sm text-graphite leading-relaxed pt-3">{item.a}</p>
              </details>
            </Reveal>
          ))}
        </div>

        {/* Bandeau « aucun frais de levée » — conservé (navy sobre) */}
        <Reveal delay={200}>
          <div
            className="mt-12 p-6 lg:p-7 border-l-2 border-gold bg-navy-deep text-ivory"
            data-testid="homepage-pricing-no-fee-note"
          >
            <p className="font-heading text-[1.1rem] lg:text-[1.2rem] text-ivory mb-1.5">
              {v('pricing.no_fee_title', fr ? 'Aucun frais de levée de fonds.' : 'No fundraising fees.')}
            </p>
            <p className="text-ivory/70 text-[13px] leading-relaxed max-w-3xl">
              {v('pricing.no_fee_body', fr
                ? 'Notre rémunération est strictement adossée à la structuration et à l\u2019administration du véhicule. Le capital apporté par vos investisseurs n\u2019est jamais ponctionné par des commissions de placement.'
                : 'Our compensation is strictly tied to structuring and administering the vehicle. Capital contributed by your investors is never subject to placement fees.')}
            </p>
          </div>
        </Reveal>

        {/* Notes prudentielles — wording réglementaire transporté tel quel */}
        <p className="text-xs text-graphite/80 leading-relaxed mt-6 max-w-4xl" data-testid="homepage-pricing-variation-note">
          {v('pricing.variation_note', fr
            ? 'Les tarifs peuvent varier selon la complexité du projet, la juridiction retenue, le nombre d\u2019investisseurs, les prestataires mobilisés et les besoins spécifiques du véhicule.'
            : 'Pricing may vary depending on the complexity of the project, the selected jurisdiction, the number of investors, the service providers mobilised and the specific needs of the vehicle.')}
        </p>
        <p className="text-xs italic text-graphite/80 leading-relaxed mt-3 max-w-4xl" data-testid="homepage-pricing-regulatory-note">
          {v('pricing.regulatory_note', fr
            ? 'Les prestations réglementées éventuellement nécessaires sont assurées par les partenaires ou professionnels habilités concernés.'
            : 'Any regulated services possibly required are delivered by the relevant authorised partners or licensed professionals.')}
        </p>

        <div className="mt-12">
          <Link
            to={paths.createSpv}
            className="inline-flex items-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-[background-color,transform] duration-200 hover:-translate-y-px"
            data-testid="pricing-cta"
          >
            {v('pricing.cta_label', fr ? 'Discuter de mon projet' : 'Discuss my project')}
            <ArrowRight size={16} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ═══ CODA (Phase II · REFINE) — la boucle cinématique ═════════════════
   Le récit se referme sur l'image qui l'a ouvert : le poster du film de
   marque (matière réelle de la maison), posé en filigrane sous un voile
   navy — rappel du Hero, aucune nouvelle ressource (image déjà en cache).
   CTAs et routes strictement inchangés. */
function CodaSection({ fr, paths, v }) {
  return (
    <section className="relative bg-navy-950 py-28 lg:py-44 overflow-hidden grain" data-testid="final-cta-section">
      <img
        src={v('hero.film_poster', '/images/brand/film/film_poster.webp')}
        alt=""
        aria-hidden
        loading="lazy"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover opacity-[0.16] motion-reduce:opacity-[0.16]"
      />
      <span className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/55" aria-hidden />
      <span className="absolute inset-x-0 top-0 h-px bg-gold/40" aria-hidden />
      <div className="container-editorial relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <div className="mb-8 opacity-90">
                <WMLogo variant="dark" mode="markOnly" size="md" />
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading font-medium text-ivory text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] leading-[1.08] tracking-[-0.01em] mb-6 text-balance">
                {v('coda.title', fr ? 'Le reste se dit de vive voix.' : 'The rest is said in person.')}
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="text-ivory/70 text-base lg:text-lg leading-relaxed max-w-2xl">
                {v('coda.body', fr
                  ? 'Chaque relation commence par une conversation — sur votre situation, votre horizon, ou l\u2019opération que vous portez. Nous répondons sous 48 heures ouvrées.'
                  : 'Every relationship starts with a conversation — about your situation, your horizon, or the operation you are carrying. We reply within 48 business hours.')}
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-4">
            <Reveal delay={240}>
              <Link
                to={paths.contact}
                className="inline-flex w-full items-center justify-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory bg-gold-dark hover:bg-gold-dark/90 transition-[background-color,transform] duration-200 hover:-translate-y-px"
                data-testid="final-cta-discuss"
              >
                {v('coda.cta_primary', fr ? 'Engager la conversation' : 'Start the conversation')}
                <ArrowRight size={16} weight="bold" />
              </Link>
            </Reveal>
            <Reveal delay={320}>
              <Link
                to={paths.requestAccess}
                className="inline-flex w-full items-center justify-center gap-3 px-7 py-4 text-sm tracking-[0.05em] uppercase font-medium text-ivory border border-ivory/30 hover:border-gold hover:text-gold transition-colors duration-200"
                data-testid="final-cta-investor-access"
              >
                {v('coda.cta_secondary', fr ? 'Demander l\u2019accès investisseur' : 'Request investor access')}
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
      <span className="absolute inset-x-0 bottom-0 h-px bg-gold/25" aria-hidden />
    </section>
  );
}

/* ═══ 04 · LA MÉTHODE (Phase II · RECOMPOSE) ═══════════════════════════
   Les quatre temps quittent la timeline verticale générique pour une
   grille éditoriale à filets — numérotation romaine imposante, tension
   typographique, points de preuve en registre. Contenu strictement
   identique (Registre R-02 à R-05, arbitrage PO 1a). */
function MethodeSection({ fr, paths }) {
  const steps = fr ? [
    {
      title: 'Sélectionner',
      desc: 'Peu de dossiers passent l\u2019étude. Visites sur place, contre-expertises, vérification des titres\u00A0: la décision d\u2019investir se prend documents en main — et se refuse de même.',
      proofs: ['Une opération retenue sur vingt étudiées', 'La maison co-investit dans chaque opération retenue'],
    },
    {
      title: 'Structurer',
      desc: 'Chaque opération retenue est logée dans un véhicule dédié, gouverné par écrit, domicilié là où il s\u2019administre. La documentation est complète avant le premier euro appelé.',
      proofs: ['Un véhicule dédié par opération', 'Gouvernance et documentation écrites'],
    },
    {
      title: 'Opérer',
      desc: 'L\u2019entrée de chaque investisseur suit le même protocole\u00A0: vérification d\u2019identité (KYC/KYB), souscription documentée, signature, virement suivi. Chaque étape laisse une trace datée dans l\u2019espace privé.',
      proofs: ['Vérification KYC/KYB systématique', 'Souscriptions et virements suivis en ligne'],
    },
    {
      title: 'Rendre compte',
      desc: 'La maison administre dans la durée\u00A0: comptes rendus réguliers, chaque distribution suivie au centime près, chaque document généré puis archivé, chaque échange conservé.',
      proofs: ['Reporting périodique dans l\u2019espace privé', 'Documents générés et archivés par la maison'],
    },
  ] : [
    {
      title: 'Select',
      desc: 'Few files survive the review. Site visits, counter-appraisals, title verification: the decision to invest is taken documents in hand — and declined the same way.',
      proofs: ['One operation retained out of twenty reviewed', 'The house co-invests in every operation retained'],
    },
    {
      title: 'Structure',
      desc: 'Each retained operation lives in a dedicated vehicle, governed in writing, domiciled where it is administered. Documentation is complete before the first euro is called.',
      proofs: ['One dedicated vehicle per operation', 'Written governance and documentation'],
    },
    {
      title: 'Operate',
      desc: 'Every investor\u2019s entry follows the same protocol: identity verification (KYC/KYB), documented subscription, signature, tracked transfer. Each step leaves a dated trace in the private space.',
      proofs: ['Systematic KYC/KYB verification', 'Subscriptions and transfers tracked online'],
    },
    {
      title: 'Report',
      desc: 'The house administers over time: regular reporting, every distribution tracked to the cent, every document generated then archived, every exchange kept on file.',
      proofs: ['Periodic reporting inside the private space', 'Documents generated and archived by the house'],
    },
  ];
  const NUMERALS = ['I', 'II', 'III', 'IV'];

  return (
    <section className="relative bg-white border-y border-stone/60 py-28 lg:py-40" data-testid="methode-section">
      <Folio n="04" />
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <Reveal>
              <Overline>{fr ? 'La méthode' : 'The method'}</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading font-medium text-ink text-[2.1rem] sm:text-[2.6rem] lg:text-[2.9rem] leading-[1.1] tracking-[-0.01em] mb-6 text-balance" data-testid="methode-title">
                {fr ? 'Quatre temps, toujours dans le même ordre.' : 'Four movements, always in the same order.'}
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="text-graphite text-base leading-relaxed max-w-sm mb-8">
                {fr
                  ? 'Ce que nous faisons ne change pas d\u2019une opération à l\u2019autre. C\u2019est précisément ce qui rend chaque opération différente possible.'
                  : 'What we do does not change from one operation to the next. That is precisely what makes each different operation possible.'}
              </p>
            </Reveal>
            <Reveal delay={260}>
              <Link
                to={paths.howItWorks}
                className="inline-flex items-center gap-2 text-sm tracking-[0.06em] uppercase font-medium text-gold-dark hover:text-ink transition-colors duration-200"
                data-testid="methode-cta"
              >
                {fr ? 'Le fonctionnement, de bout en bout' : 'How it works, end to end'}
                <ArrowRight size={14} weight="bold" />
              </Link>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <ol className="grid grid-cols-1 md:grid-cols-2 border-t border-stone" data-testid="methode-steps">
              {steps.map((s, i) => (
                <li
                  key={s.title}
                  className={`relative border-b border-stone py-10 md:py-12 ${i % 2 === 0 ? 'md:pr-12 md:border-r' : 'md:pl-12'}`}
                  data-testid={`methode-step-${i + 1}`}
                >
                  <Reveal delay={(i % 2) * 130}>
                    <div className="flex items-baseline justify-between gap-4 mb-5">
                      <span className="font-heading italic text-gold-dark/50 text-[2.8rem] lg:text-[3.2rem] leading-none select-none" aria-hidden>
                        {NUMERALS[i]}
                      </span>
                      <span className="block flex-1 h-px bg-stone/80 translate-y-[-8px]" aria-hidden />
                    </div>
                    <h3 className="font-heading text-charcoal text-[1.45rem] lg:text-[1.6rem] leading-tight mb-4">{s.title}</h3>
                    <p className="text-graphite/85 text-[14.5px] leading-relaxed">{s.desc}</p>
                    <ul className="mt-5 space-y-1.5">
                      {s.proofs.map((p) => (
                        <li key={p} className="flex items-baseline gap-2.5 text-[12.5px] text-graphite/70 leading-relaxed">
                          <span className="inline-block w-3 h-px bg-gold-dark/60 shrink-0 translate-y-[-3px]" aria-hidden />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══ CITATION (INSTITUTIONAL DEPTH V1 · LOT 2) — respiration ══════════
   Registre des affirmations : R-06 (énoncé de conviction, À VALIDER PO). */
function CitationSection({ fr }) {
  return (
    <section className="relative bg-parchment py-28 lg:py-40" data-testid="citation-section" aria-label={fr ? 'Citation' : 'Quote'}>
      <div className="container-editorial">
        <div className="max-w-4xl lg:ml-[10%]">
          <PullQuote
            testid="citation-quote"
            quote={fr
              ? 'La confiance ne se demande pas. Elle se construit.'
              : 'Trust is not asked for. It is built.'}
            attribution={fr ? 'Principe de la maison' : 'A house principle'}
          />
        </div>
      </div>
    </section>
  );
}

/* ═══ L'ESPACE PRIVÉ — PUBLIC EXPERIENCE V2 ════════════════════════════
   Six capacités OBSERVABLES du produit (aucune inventée) : consolidation
   patrimoniale, documents & fiscalité, mouvements & distributions,
   messagerie, notifications & calendrier, reporting. Chaque exemple
   correspond à un écran réel de l'espace investisseur.
   Registre : R-07 (V2). */
function ApercuPriveSection({ fr }) {
  const items = fr ? [
    {
      title: 'Reporting patrimonial consolidé',
      desc: 'Vos positions, tous véhicules confondus, tenues au centime sur une seule page.',
      points: ['Valeur par opération et vue d\u2019ensemble', 'Historique complet depuis la première souscription'],
    },
    {
      title: 'Documents & fiscalité',
      desc: 'Chaque pièce générée par la maison, classée et datée — rien à réclamer.',
      points: ['Attestations de souscription et relevés', 'Supports fiscaux annuels, archivés à date', 'Statuts, pactes et bulletins du véhicule'],
    },
    {
      title: 'Mouvements & distributions',
      desc: 'Chaque flux tracé, du virement initial à la dernière distribution.',
      points: ['Virements rapprochés puis confirmés', 'Distributions suivies au centime', 'Historique conservé, du premier virement à la clôture'],
    },
    {
      title: 'Messagerie de la maison',
      desc: 'Vos échanges avec la maison, au même endroit que vos dossiers.',
      points: ['Conversations datées et conservées', 'Chaque échange rattaché à votre dossier'],
    },
    {
      title: 'Notifications & calendrier',
      desc: 'Des dates réelles, inscrites par la maison. Jamais de projections.',
      points: ['Échéances des opérations en cours', 'Préférences de notification réglables'],
    },
    {
      title: 'Comptes rendus périodiques',
      desc: 'Le travail de la maison, rendu par écrit — opération par opération.',
      points: ['Reporting périodique et annuel', 'Consultable et archivé dans l\u2019espace'],
    },
  ] : [
    {
      title: 'Consolidated wealth reporting',
      desc: 'Your positions, across all vehicles, kept to the cent on a single page.',
      points: ['Value per operation and overall view', 'Full history since your first subscription'],
    },
    {
      title: 'Documents & taxation',
      desc: 'Every document generated by the house, filed and dated — nothing to chase.',
      points: ['Subscription certificates and statements', 'Annual tax supports, archived as of date', 'The vehicle\u2019s articles, agreements and forms'],
    },
    {
      title: 'Movements & distributions',
      desc: 'Every flow traced, from the initial transfer to the final distribution.',
      points: ['Transfers reconciled then confirmed', 'Distributions tracked to the cent', 'History kept, from first transfer to closing'],
    },
    {
      title: 'House messaging',
      desc: 'Your exchanges with the house, in the same place as your files.',
      points: ['Conversations dated and kept', 'Every exchange attached to your file'],
    },
    {
      title: 'Notifications & calendar',
      desc: 'Real dates, entered by the house. Never projections.',
      points: ['Maturities of current operations', 'Adjustable notification preferences'],
    },
    {
      title: 'Periodic reporting',
      desc: 'The house\u2019s work, rendered in writing — operation by operation.',
      points: ['Periodic and annual reporting', 'Consultable and archived in the space'],
    },
  ];

  return (
    <section className="relative bg-ivory py-28 lg:py-36" data-testid="apercu-prive-section">
      <div className="container-editorial">
        <EditorialDivider className="mb-16 lg:mb-20" />
        <div className="max-w-3xl mx-auto text-center mb-14 lg:mb-20">
          <Reveal>
            <Overline center>{fr ? 'L\u2019espace privé' : 'The private space'}</Overline>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-heading font-medium text-ink text-[2.1rem] sm:text-[2.6rem] lg:text-[2.9rem] leading-[1.1] tracking-[-0.01em] text-balance" data-testid="apercu-prive-title">
              {fr ? 'Ce qui vous attend derrière la porte.' : 'What awaits behind the door.'}
            </h2>
          </Reveal>
          <Reveal delay={180}>
            <p className="text-graphite text-base leading-relaxed mt-5">
              {fr
                ? 'L\u2019espace privé n\u2019est pas une vitrine\u00A0: c\u2019est l\u2019outil de travail que la maison partage avec ses investisseurs. Tout ce qui suit existe, tel quel.'
                : 'The private space is not a showcase: it is the working tool the house shares with its investors. Everything below exists, as is.'}
            </p>
          </Reveal>
        </div>
        {/* Phase II · REFINE — registre à filets (plus de cards boxées) :
            chaque capacité s'ouvre sur un filet stone, numéral éditorial,
            points de preuve en liste discrète. Textes et testids inchangés. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 lg:gap-x-14 gap-y-12 lg:gap-y-14 max-w-6xl mx-auto">
          {items.map((it, i) => (
            <Reveal key={it.title} delay={(i % 3) * 90}>
              <div className="border-t border-stone pt-7 h-full group" data-testid={`apercu-prive-item-${i}`}>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="font-heading italic text-gold-dark/70 text-lg leading-none lining-nums group-hover:text-gold-dark transition-colors duration-300">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="block w-8 h-px bg-gold-dark/40 group-hover:w-14 transition-[width] duration-500" aria-hidden />
                </div>
                <h3 className="font-heading text-lg lg:text-xl text-charcoal leading-snug mt-5 mb-3">{it.title}</h3>
                <p className="text-sm text-graphite/85 leading-relaxed mb-4">{it.desc}</p>
                <ul className="space-y-1.5">
                  {it.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-2.5 text-[12.5px] text-graphite/75 leading-relaxed">
                      <span className="inline-block w-3 h-px bg-gold-dark/60 mt-2.5 shrink-0" aria-hidden />
                      <span>{pt}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={300}>
          <p className="text-center mt-14 text-sm text-graphite/80 leading-relaxed" data-testid="apercu-prive-note">
            {fr
              ? 'L\u2019accès s\u2019ouvre après examen de votre demande — le parcours est décrit ci-dessus, étape par étape.'
              : 'Access opens after your request is reviewed — the path is described above, step by step.'}
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ═══ 07 · LE JOURNAL — CONTENU / notre lecture des marchés (Phase II) ══
   Nouveau mouvement validé PO (05/09) : la maison montre qu'elle LIT ses
   marchés avant d'y engager du capital. UNIQUEMENT des articles réels —
   les trois derniers publiés, servis par l'API editorial (published only,
   FR/EN, images réelles des articles). Grande story + deux secondaires en
   composition typographique. Fail-soft : sans matière réelle suffisante,
   le mouvement ne se rend pas. Maillage interne : liens localisés vers
   les articles et le hub — bonus SEO, aucun impact sitemap.
   CLÔTURE BACKLOG (07/09/2026) — épinglage éditorial : si le Studio
   renseigne `journal.pinned_slug` (slug d'un article publié), cet article
   devient la grande story ; les secondaires restent les plus récents.
   Slug introuvable/dépublié → fallback strict au comportement d'origine
   (3 derniers réels). Jamais d'article fictif. */
function JournalSection({ fr, v, lang }) {
  const publicData = usePublicPageData();
  const pinnedSlug = (v('journal.pinned_slug', '') || '').trim();
  const [articles, setArticles] = useState(() => {
    const items = publicData?.journal?.items;
    if (!items) return null;
    const pinned = items.find(article => article.slug === pinnedSlug);
    return pinned ? [pinned, ...items.filter(article => article.slug !== pinnedSlug)].slice(0, 3) : items.slice(0, 3);
  });

  useEffect(() => {
    let alive = true;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/editorial/public/articles`, {
      params: { locale: lang },
    })
      .then((res) => {
        if (!alive) return;
        const items = res.data?.items || [];
        const latest = items.slice(0, 3);
        let selection = latest;
        if (pinnedSlug) {
          const pinned = items.find((a) => a.slug === pinnedSlug);
          if (pinned) {
            selection = [pinned, ...latest.filter((a) => a.slug !== pinnedSlug)].slice(0, 3);
          }
        }
        setArticles(selection);
      })
      .catch(() => { if (alive) setArticles([]); });
    return () => { alive = false; };
  }, [lang, pinnedSlug]);

  // État honnête : sans articles réels, pas de section.
  if (!articles || articles.length < 2) return null;
  const [lead, ...rest] = articles;
  const journalPath = `/${lang}/journal`;

  const Meta = ({ a }) => (
    <p className="flex flex-wrap items-baseline gap-3 text-[12px] text-graphite/75">
      <span>{formatJournalDate(a.published_at, lang)}</span>
      {a.reading_minutes ? (
        <>
          <span className="inline-block w-3 h-px bg-stone self-center" aria-hidden />
          <span>{a.reading_minutes} {fr ? 'min de lecture' : 'min read'}</span>
        </>
      ) : null}
    </p>
  );

  return (
    <section className="relative bg-white border-y border-stone/60 py-28 lg:py-40" data-testid="journal-section">
      <Folio n="07" />
      <div className="container-editorial">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-end mb-14 lg:mb-16">
          <div className="lg:col-span-8">
            <Reveal>
              <Overline>{v('journal.overline', fr ? 'Le journal de la maison' : 'The house journal')}</Overline>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-heading font-medium text-ink text-[2.1rem] sm:text-[2.6rem] lg:text-[2.9rem] leading-[1.1] tracking-[-0.01em] text-balance" data-testid="journal-home-title">
                {v('journal.title', fr ? 'Notre lecture des marchés.' : 'Our reading of the markets.')}
              </h2>
            </Reveal>
            <Reveal delay={180}>
              <p className="text-graphite text-base lg:text-lg leading-relaxed max-w-2xl mt-5">
                {v('journal.lede', fr
                  ? 'Ce que nous apprenons en construisant, en structurant et en refusant — écrit pour être utile, pas pour vendre.'
                  : 'What we learn while building, structuring and declining — written to be useful, not to sell.')}
              </p>
            </Reveal>
          </div>
          <Reveal delay={220} className="lg:col-span-4 lg:text-right">
            <Link
              to={journalPath}
              className="inline-flex items-center gap-3 text-sm tracking-[0.05em] uppercase font-medium text-gold-dark hover:text-ink transition-colors duration-200"
              data-testid="journal-home-cta"
            >
              {v('journal.cta', fr ? 'Parcourir le journal' : 'Browse the journal')}
              <ArrowRight size={16} weight="bold" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Grande story — image réelle de l'article */}
          <Link
            to={`/${lang}/journal/${lead.slug}`}
            className="lg:col-span-7 group block"
            data-testid="journal-home-lead"
          >
            <EditorialImage
              src={lead.cover_image}
              alt={lead.cover_alt || lead.title}
              ratio="3/2"
            />
            <p className="text-[11px] tracking-[0.16em] uppercase font-medium text-gold-dark mt-6 mb-3">
              {lead.category_label || lead.category}
            </p>
            <h3 className="font-heading font-medium text-ink text-[1.6rem] lg:text-[1.9rem] leading-[1.15] mb-4 transition-colors duration-200 group-hover:text-gold-dark text-balance">
              {lead.title}
            </h3>
            {lead.excerpt && (
              <p className="text-graphite text-[15px] leading-relaxed mb-4 max-w-xl">
                {lead.excerpt}
              </p>
            )}
            <Meta a={lead} />
          </Link>

          {/* Secondaires — composition typographique à filets */}
          <div className="lg:col-span-5 flex flex-col lg:pt-2">
            {rest.map((a, i) => (
              <Reveal key={a.slug} delay={120 * (i + 1)}>
                <Link
                  to={`/${lang}/journal/${a.slug}`}
                  className="group block border-t border-stone py-8 lg:py-9"
                  data-testid={`journal-home-item-${i + 1}`}
                >
                  <p className="text-[11px] tracking-[0.16em] uppercase font-medium text-gold-dark mb-3">
                    {a.category_label || a.category}
                  </p>
                  <h3 className="font-heading font-medium text-ink text-[1.25rem] lg:text-[1.45rem] leading-[1.2] mb-3 transition-colors duration-200 group-hover:text-gold-dark text-balance">
                    {a.title}
                  </h3>
                  {a.excerpt && (
                    <p className="text-graphite/85 text-sm leading-relaxed mb-4 line-clamp-3">
                      {a.excerpt}
                    </p>
                  )}
                  <Meta a={a} />
                </Link>
              </Reveal>
            ))}
            <span className="block border-t border-stone" aria-hidden />
          </div>
        </div>
      </div>
    </section>
  );
}

/* Date d'article — même convention que le hub Journal (Intl, fail-soft). */
function formatJournalDate(iso, lang) {
  if (!iso) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'fr-FR',
      { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
